import { Eye, MapPin } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Button,
} from '../ui'
import type { AttendanceEvent } from '../../types/attendance.types'
import { formatDateTime } from '../../utils/dateFormat'

interface RecentCheckInsTableProps {
  events: AttendanceEvent[]
  onViewDetails?: (event: AttendanceEvent) => void
}

export function RecentCheckInsTable({ events, onViewDetails }: RecentCheckInsTableProps) {
  const getEmployeeName = (event: AttendanceEvent) => {
    if (!event.employee) return 'Не указан'
    const { first_name, last_name, middle_name } = event.employee
    return [last_name, first_name, middle_name].filter(Boolean).join(' ')
  }

  const getStatusBadge = (event: AttendanceEvent) => {
    // Для определения опоздания нужно получить данные из time_records
    // Пока API не возвращает is_late, показываем временную заглушку
    // В будущем это можно будет получить из связанной записи time_records
    
    // Временная логика: считаем опозданием если пришел после 9:15
    const occurredDate = new Date(event.occurred_at)
    const hours = occurredDate.getHours()
    const minutes = occurredDate.getMinutes()
    const timeInMinutes = hours * 60 + minutes
    const standardStartTime = 9 * 60 + 15 // 9:15
    
    const isLate = timeInMinutes > standardStartTime
    
    if (isLate) {
      return (
        <Badge variant="default" className="bg-red-100 text-red-800">
          Опоздание
        </Badge>
      )
    }
    
    return (
      <Badge variant="default" className="bg-green-100 text-green-800">
        Вовремя
      </Badge>
    )
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Сотрудник</TableHead>
            <TableHead>Время прихода</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead>Локация</TableHead>
            <TableHead className="text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                Нет данных о последних отметках
              </TableCell>
            </TableRow>
          ) : (
            events.map((event) => {
              const dateTime = formatDateTime(event.occurred_at)
              return (
                <TableRow key={event.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium">{getEmployeeName(event)}</p>
                      {event.employee && (
                        <div className="flex flex-col text-sm text-muted-foreground">
                          {event.employee.position && (
                            <span className="text-xs">{event.employee.position.name}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium">{dateTime.time}</p>
                      <p className="text-xs text-muted-foreground">{dateTime.date}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(event)}</TableCell>
                  <TableCell>
                    {event.location ? (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-muted-foreground" />
                        <p className="text-sm">{event.location.name}</p>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Не указана</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {onViewDetails && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Просмотр деталей"
                        onClick={() => onViewDetails(event)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}






